import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

type CookieConsentContextType = {
  hasConsent: boolean;
  showBanner: boolean;
  cookiePreferences: CookiePreferences;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  showPreferencesDialog: boolean;
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const STORAGE_KEY = 'cookie-consent-preferences';
const CONSENT_VERSION = '1.0';

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>({
    necessary: true, // Always true
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Load saved preferences on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem(STORAGE_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setCookiePreferences(parsed.preferences);
          setHasConsent(true);
          setShowBanner(false);
        } else {
          // Version mismatch - show banner again
          setShowBanner(true);
        }
      } catch (error) {
        console.error('Failed to parse cookie preferences:', error);
        setShowBanner(true);
      }
    } else {
      // No saved preferences - show banner
      setShowBanner(true);
    }
  }, []);

  const saveToStorage = (preferences: CookiePreferences) => {
    const data = {
      version: CONSENT_VERSION,
      preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const saveCookieConsentToDatabase = async (preferences: CookiePreferences) => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const ipResponse = await fetch('https://api.ipify.org?format=json').catch(() => null);
      const ipData = ipResponse ? await ipResponse.json() : null;
      
      const consents = [
        { type: 'cookies_analytics', given: preferences.analytics },
        { type: 'cookies_marketing', given: preferences.marketing },
        { type: 'cookies_preferences', given: preferences.preferences }
      ];
      
      for (const consent of consents) {
        await (supabase as any).from('consent_records').insert({
          user_id: user.id,
          consent_type: consent.type,
          consent_given: consent.given,
          consent_version: CONSENT_VERSION,
          ip_address: ipData?.ip,
          user_agent: navigator.userAgent
        });
      }
    } catch (error) {
      console.error('Error saving cookie consent to database:', error);
    }
  };

  const acceptAll = async () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setCookiePreferences(allAccepted);
    saveToStorage(allAccepted);
    await saveCookieConsentToDatabase(allAccepted);
    setHasConsent(true);
    setShowBanner(false);
    setShowPreferencesDialog(false);
  };

  const rejectAll = async () => {
    const allRejected: CookiePreferences = {
      necessary: true, // Necessary cookies cannot be rejected
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setCookiePreferences(allRejected);
    saveToStorage(allRejected);
    await saveCookieConsentToDatabase(allRejected);
    setHasConsent(true);
    setShowBanner(false);
    setShowPreferencesDialog(false);
  };

  const savePreferences = async (preferences: CookiePreferences) => {
    // Ensure necessary is always true
    const validPreferences = { ...preferences, necessary: true };
    setCookiePreferences(validPreferences);
    saveToStorage(validPreferences);
    await saveCookieConsentToDatabase(validPreferences);
    setHasConsent(true);
    setShowBanner(false);
    setShowPreferencesDialog(false);
  };

  const openPreferences = () => {
    setShowPreferencesDialog(true);
  };

  const closePreferences = () => {
    setShowPreferencesDialog(false);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        hasConsent,
        showBanner,
        cookiePreferences,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
        showPreferencesDialog,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};
